/**
 * Shared API Utilities
 * Following Feature-Based Modular Architecture
 */

import {
  translateWithFallback,
  type TranslateWithDefaultValue,
} from '@platform/i18n/translation-resolution'

export interface ApiResponse<T = unknown> {
  data: T
  success: boolean
  message?: string
  errors?: string[]
}

export interface ApiError {
  status: number
  message: string
  code?: string
  details?: unknown
  /** The backend's request id (error envelope `requestId`): what an operator quotes to find the log line. */
  requestId?: string
}

export class ApiException extends Error {
  public status: number
  public code?: string
  public details?: unknown
  public requestId?: string

  constructor(error: ApiError) {
    super(error.message)
    this.name = 'ApiException'
    this.status = error.status
    if (error.code !== undefined) {
      this.code = error.code
    }
    if (error.details !== undefined) {
      this.details = error.details
    }
    if (error.requestId !== undefined) {
      this.requestId = error.requestId
    }
  }
}

// Note: createApiError is kept (not exported-for-consumers, but used internally
// by handleApiError below) — it is NOT dead despite having no external importers.
export const createApiError = (
  status: number,
  message: string,
  code?: string,
  details?: Record<string, unknown> | Array<{ field: string; message: string; code?: string }>,
): ApiError => ({
  status,
  message,
  ...(code !== undefined ? { code } : {}),
  ...(details !== undefined ? { details } : {}),
})

/** i18n keys (in the `errors` namespace) for synthetic transport errors. */
type SyntheticErrorI18nKey =
  | 'network'
  | 'bad_response'
  | 'timeout'
  | 'token_expired'
  | 'upload_error'
  | 'unexpected'

/** Synthetic client-side error code → i18n key in the `errors` namespace. */
const SYNTHETIC_CODE_TO_ERROR_I18N_KEY: Record<string, SyntheticErrorI18nKey> = {
  NETWORK_ERROR: 'network',
  BAD_RESPONSE: 'bad_response',
  TIMEOUT: 'timeout',
  TOKEN_EXPIRED: 'token_expired',
  UPLOAD_ERROR: 'upload_error',
}

/**
 * Backend business-error CODE → i18n key in the `errors` namespace.
 *
 * These codes are thrown by the backend with a HARDCODED English message at the
 * throw site (e.g. MFA `verifyAndEnableMfa` throws AuthError('Invalid TOTP
 * code')), so their inline `message` is NOT localized. We map the code to a key
 * in the (HTTP-loaded) `errors` catalog and prefer the catalog's localized copy
 * over the English inline message — the user sees "Código incorrecto", not raw
 * English. Falls back to the backend message when the catalog is unavailable
 * (never a raw i18n key). Same mechanism as the synthetic/stripe code maps.
 */
const BACKEND_CODE_TO_ERROR_I18N_KEY: Record<string, string> = {
  // Ownership refusals across the import module (`template.handlers.ts`,
  // `job-crud.handler.ts`) and the tax trail. All of them throw a HARDCODED
  // English message at the throw site ("Access denied to this template"), which
  // leaked verbatim into the Spanish UI — e.g. "Error al guardar template:
  // Access denied to this template" when a user tried to save a GLOBAL import
  // template.
  ACCESS_DENIED: 'access_denied',
  // Super-admin exchange catalog: creating a slug that already exists returns
  // 409 with the hardcoded English "Exchange with slug 'kraken' already exists"
  // (admin/features/exchanges/api/handlers.ts). That string reached a Spanish
  // UI verbatim; the catalog entry replaces it.
  EXCHANGE_EXISTS: 'exchange_exists',
  // Deleting/deactivating an exchange that configured imports still use: 409
  // with «Cannot delete exchange 'qa21-bolsa-norte': 1 data source(s) are
  // using this exchange» — English, the slug and a «(s)», verbatim in the
  // Spanish admin (measured 2026-09-23).
  EXCHANGE_IN_USE: 'exchange_in_use',
  // Saving a global CSV template under a name another one already has: 409
  // «A global import mapping named 'X' already exists», verbatim after
  // «Error al guardar la plantilla:» in the Spanish editor (2026-09-23).
  DUPLICATE_MAPPING_NAME: 'duplicate_mapping_name',
  // Super-admin blockchain templates: the create route refuses a second ACTIVE
  // template for the same chain / a duplicate slug with a 400 whose message is
  // hardcoded English AND interpolates the internal slug ("Template for
  // blockchain 'ethereum' already exists"). Unmapped, the create modal fell all
  // the way through to its generic "no se pudo crear" sentence, so the admin was
  // never told WHY — see NewBlockchainModal.
  BLOCKCHAIN_TEMPLATE_EXISTS: 'blockchain_template_exists',
  BLOCKCHAIN_TEMPLATE_SLUG_EXISTS: 'blockchain_template_slug_exists',
  // Deleting a blockchain template marked for production is refused with a
  // hardcoded English sentence that interpolates the template name. Unmapped,
  // it reached the Spanish admin verbatim.
  BLOCKCHAIN_TEMPLATE_IS_PRODUCTION_READY: 'blockchain_template_is_production_ready',
  // Super-admin blockchain template EDITOR rejections. Each is thrown with a
  // hardcoded English sentence that interpolates the offending value
  // ("Invalid transaction attribute key: este-atributo-no-existe"), which
  // reached the Spanish import modal verbatim.
  // Super-admin CSV mapping editor: a rule whose regex does not compile is
  // refused with an English sentence that interpolates the pattern and column
  // ("Rule regex does not compile … \"[sin-cerrar\" (buy_coin)"). The editor
  // already flags the offending field in red, so the catalog sentence points
  // there instead of repeating the raw pattern.
  INVALID_RULE_REGEX: 'invalid_rule_regex',
  BLOCKCHAIN_TEMPLATE_INVALID_ATTRIBUTE_KEY: 'blockchain_template_invalid_attribute_key',
  BLOCKCHAIN_TEMPLATE_INVALID_ADDRESS: 'blockchain_template_invalid_address',
  BLOCKCHAIN_TEMPLATE_INVALID_MATCH_CRITERIA: 'blockchain_template_invalid_match_criteria',
  BLOCKCHAIN_TEMPLATE_DUPLICATE_ENTRY: 'blockchain_template_duplicate_entry',
  BLOCKCHAIN_TEMPLATE_INVALID_HIERARCHY: 'blockchain_template_invalid_hierarchy',
  // El sobre de `parseDtoOrThrow` (blockchain-templates/api/handlers.ts): SIEMPRE
  // «Validation failed for body|query|params» —una frase inglesa que no nombra
  // ningún campo— mientras toda la información viaja en `details[]`. Sin esta
  // fila, el modal de «Config servidor» pintaba ese inglés tal cual en una
  // pantalla en español (Puerto=8545, Timeout=-5 → «Validation failed for body»).
  //
  // ⚠️ El GEMELO de este código, `VALIDATION_ERROR`, NO se mapea aquí a
  // propósito: no es un centinela. Lo emiten decenas de rutas con frases
  // REDACTADAS por su autor («Configurations "Operaciones" y "Movimientos"
  // comparten ≥80%…») que `carriesAuthoredCopy` hace ganar deliberadamente;
  // sustituirlas todas por una sola frase genérica borraría esa información en
  // 218 rechazos. Lo que un rechazo por campo necesita —el NOMBRE del campo en
  // pantalla— no puede salir de este mapa, porque `details[].field` es la clave
  // interna del payload: lo resuelve `shared/utils/backend-field-rejection.ts`,
  // con el catálogo de etiquetas que declara cada pantalla.
  BLOCKCHAIN_TEMPLATE_VALIDATION_FAILED: 'blockchain_template_validation_failed',
  // «Probar» en el editor de configuración API de un exchange. 404 SIN
  // `resourceKind`, así que `resolveResourceNotFoundMessage` lo deja pasar, y su
  // mensaje («No enabled endpoints found for exchange 'kraken'») es inglés e
  // interpola el slug (exchange-config.handlers.ts).
  NO_ENDPOINTS: 'exchange_api_config_no_endpoints',
  // RPC probe failures (POST /test-rpc). The backend composes an English
  // sentence from the underlying fetch error — "RPC connection failed: The
  // operation was aborted due to timeout" — and sends a stable code beside it.
  RPC_CONFIG_INCOMPLETE: 'rpc.config_incomplete',
  RPC_TIMEOUT: 'rpc.timeout',
  RPC_HTTP_ERROR: 'rpc.http_error',
  RPC_NODE_ERROR: 'rpc.node_error',
  RPC_UNREACHABLE: 'rpc.unreachable',
  // The unique-constraint refusal every transaction write can hit
  // (transaction-write.repository.ts maps SQLSTATE 23505 → 409 with the
  // HARDCODED English "A transaction with the same identifier already exists").
  // It is what a bulk "Duplicar" gets when two rows of identical content race
  // for the same free millisecond slot, and it reached the Spanish UI verbatim
  // because no catalog entry existed for the code.
  DUPLICATE_TRANSACTION: 'transactions_duplicate',
  INVALID_MFA_CODE: 'mfa.invalid_code',
  MFA_ALREADY_ENABLED: 'mfa.already_enabled',
  // Transaction-matching (Traza) business errors. The matching services throw
  // these codes with a HARDCODED English message at the throw site (see
  // transaction-matching-resolution/-detection.service.ts), so mapping the code
  // to the `errors:matching.*` catalog lets the UI show localized Spanish copy
  // instead of the raw English. Falls back to the backend message when the
  // catalog is unavailable (never a raw i18n key).
  TRANSACTION_NOT_FOUND: 'matching.transaction_not_found',
  ASSET_MISMATCH: 'matching.asset_mismatch',
  DEPOSIT_EXCEEDS_WITHDRAWAL: 'matching.deposit_exceeds_withdrawal',
  WITHDRAWAL_FULLY_MATCHED: 'matching.withdrawal_fully_matched',
  DEPOSIT_FULLY_MATCHED: 'matching.deposit_fully_matched',
  NO_AMOUNT_AVAILABLE: 'matching.no_amount_available',
  INVALID_TEMPORAL_ORDER: 'matching.invalid_temporal_order',
  INCOMPATIBLE_TXID_MATCH: 'matching.incompatible_txid_match',
  MATCH_NOT_FOUND: 'matching.match_not_found',
  // Classification / operation guards on the Traza page. These are thrown with a
  // HARDCODED English message (e.g. 'Operation (swap) requires DIFFERENT assets')
  // by transaction-matching-detection/-classification.service.ts.
  OPERATION_SAME_ASSET: 'matching.operation_same_asset',
  BRIDGE_DIFFERENT_ASSET: 'matching.bridge_different_asset',
  INVALID_TRANSACTION_TYPE: 'matching.invalid_transaction_type',
  INVALID_CLASSIFICATION_TYPE: 'matching.invalid_classification_type',
  INVALID_AMOUNT: 'matching.invalid_amount',
  EMPTY_TRANSACTION_LIST: 'matching.empty_transaction_list',
  // R7/R8 classification guards. Both backend messages INTERPOLATE transaction
  // UUIDs ("... Matched transactions: 34434e87-85e9-..."), so leaving either
  // unmapped shows the customer raw English AND an internal id.
  MULTIPLE_CLASSIFICATIONS: 'matching.multiple_classifications',
  CANNOT_CLASSIFY_MATCHED: 'matching.cannot_classify_matched',
  // R8 from its OTHER side: matching a transaction that already carries a
  // classification. Same invariant, same catalog treatment — without this row the
  // user would see the raw English fallback for a rejection they must act on.
  CANNOT_MATCH_CLASSIFIED: 'matching.cannot_match_classified',
  // Generic safety net: any business-rule violation whose specific rule id is not
  // localized elsewhere still shows friendly localized copy — never the raw
  // English "Business rule 'X' violated: ..." that leaks the internal rule id.
  BUSINESS_RULE_VIOLATION: 'business_rule',
  // ── Editor de PASOS DE BARRIDO (super-admin) ────────────────────────────
  // El barrido de una cadena es ahora un catálogo de filas
  // (`bat.blockchain_sweep_steps`), y su editor rechaza con cuatro códigos
  // cuyos mensajes son prosa inglesa que NOMBRA lo que falla. Sin estas filas
  // el modal caería a su frase genérica y el operador no sabría QUÉ corregir —
  // el mismo defecto que BLOCKCHAIN_TEMPLATE_EXISTS documenta más arriba.
  //
  // ⚠️ Las traducciones van en platform/errors/i18n/locales, NO en
  // platform/i18n/locales: el segundo contiene un errors.json que NADIE sirve
  // (i18n.service.ts elige el primero para el espacio `errors`), y hay 17
  // claves ya atrapadas en ese señuelo.
  SWEEP_PHASE_NOT_FOUND: 'apiErrorCodes.SWEEP_PHASE_NOT_FOUND',
  SWEEP_TRANSITION_TARGET_NOT_FOUND: 'apiErrorCodes.SWEEP_TRANSITION_TARGET_NOT_FOUND',
  SWEEP_STEP_ALREADY_EXISTS: 'apiErrorCodes.SWEEP_STEP_ALREADY_EXISTS',
  SWEEP_STEP_NOT_FOUND: 'apiErrorCodes.SWEEP_STEP_NOT_FOUND',
  VM_FAMILY_HAS_NO_SWEEP_PHASES: 'apiErrorCodes.VM_FAMILY_HAS_NO_SWEEP_PHASES',
  // Lo emite el módulo de redes desde antes que el barrido, y hasta ahora
  // llegaba al operador en el inglés crudo del backend (tarea #146). Se mapea
  // aquí porque el catálogo de pasos lo estrena por un camino nuevo: no tiene
  // sentido abrir una puerta y dejarla muda.
  VM_FAMILY_UNKNOWN: 'apiErrorCodes.VM_FAMILY_UNKNOWN',
}

/**
 * SPECIFIC business rules, keyed by the rule id the backend puts in
 * `details[].code`.
 *
 * The top-level code for every rule is `BUSINESS_RULE_VIOLATION`, which resolves
 * to one generic sentence — deliberately, so the raw English
 * "Business rule 'X' violated" can never leak the internal id. The cost was that
 * a user whose inline edit was rejected read "the operation could not be
 * completed due to a business rule" while the server had said exactly what was
 * wrong ("Transaction date cannot be in the future").
 *
 * Rules listed HERE get their own localized copy. Anything not listed falls
 * through to the generic message unchanged, so adding a rule is opt-in and can
 * never regress an existing toast.
 */
const BACKEND_RULE_CODE_TO_ERROR_I18N_KEY: Record<string, string> = {
  FUTURE_DATE: 'transactions_future_date',
  // Creating a global asset on a taken ticker. The server names the collision
  // ("Asset with symbol 'BTC' already exists") but that sentence is raw English,
  // so without this row the admin read the generic business-rule copy and had no
  // idea WHICH field to change.
  DUPLICATE_SYMBOL: 'assets_duplicate_symbol',
  DUPLICATE_CANONICAL_ID: 'assets_duplicate_canonical_id',
  // Saving a multi-configuration import mapping whose CSV shapes overlap by
  // ≥80% without discriminators. The server's sentence NAMES both
  // configurations, and it is raw English: it reached the Spanish admin
  // verbatim ("Configurations "Bitpanda CSV (16 columnas)" and … share ≥80% of
  // their expectedHeaders."). The two labels ride in `details[].value`
  // (mode-validation.ts), so the localized copy can name them too.
  overlapping_headers_without_discriminator: 'import_mappings_overlapping_headers',
}

/**
 * Which `details[].value` entries feed the localized sentence of a rule, per
 * rule code. A rule with no entry here is translated without interpolation, as
 * every rule was before this map existed.
 */
const BACKEND_RULE_CODE_TO_INTERPOLATION_KEYS: Record<string, readonly string[]> = {
  overlapping_headers_without_discriminator: ['configurationA', 'configurationB'],
}

/**
 * Last-resort English copy, mirroring the catalog the backend actually SERVES
 * for the `errors` namespace:
 * `packages/backend/src/platform/errors/i18n/locales/en/errors.json`.
 *
 * NOT `platform/i18n/locales/en/errors.json` — same filename, different file,
 * and the one the `errors` namespace is NOT loaded from
 * (`I18nService.loadTranslationFile` routes this namespace to the `errors/`
 * tree). Aiming at the wrong one is how five of these keys went missing from
 * the served catalog while the drift-guard test stayed green.
 *
 * Used ONLY when i18n cannot resolve the key — e.g.
 * the backend/proxy is down so the HTTP-loaded `errors` catalog never arrived,
 * which is EXACTLY the scenario that produces these synthetic transport codes.
 * Without this guard, i18next returns the raw key fragment (`bad_response`)
 * and the UI shows a technical code to the user (login-form bug).
 */
// Exported ONLY for the drift-guard test that compares this copy byte-for-byte
// against the SERVED backend catalog (packages/backend/src/platform/errors/
// i18n/locales/en/errors.json). Do not import from application code — use
// toBackendMessage.
export const SYNTHETIC_ERROR_FALLBACK_COPY: Record<SyntheticErrorI18nKey, string> = {
  network: 'Network error. Please check your connection and try again.',
  bad_response: 'Received an invalid response from the server',
  timeout: 'The request took too long. Please try again.',
  token_expired: 'Your session has expired. Please log in again.',
  upload_error: 'File upload failed. Please try again.',
  unexpected: 'An unexpected error occurred',
}

/** RFC-4122 id — internal, never customer-facing copy. */
const INTERNAL_IDENTIFIER_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/**
 * The backend message is only ever a FALLBACK (used when the localized catalog
 * cannot be reached), and several business errors interpolate the offending
 * transaction UUIDs into it. Keep the leading COMPLETE sentences — the part that
 * explains the problem — and drop the id-carrying tail. Returns undefined when
 * nothing id-free survives, so the caller uses generic copy instead of showing
 * an internal identifier.
 */
export const toCustomerSafeFallbackMessage = (message: string): string | undefined => {
  if (!INTERNAL_IDENTIFIER_PATTERN.test(message)) return message
  // eslint-disable-next-line local/no-hardcoded-error-fallback -- REDACTS internal ids from the fallback copy; it does not branch on the message to pick which copy to show (the code → catalog key mapping does that)
  const completeSentences = (message.match(/[^.]+\./g) ?? []).join(' ').trim()
  if (completeSentences.length === 0) return undefined
  if (INTERNAL_IDENTIFIER_PATTERN.test(completeSentences)) return undefined
  return completeSentences
}

/**
 * Resolve a key in the `errors` namespace via i18n, falling back to the given
 * hardcoded English copy when the catalog cannot resolve it (the catalog is
 * HTTP-loaded from the backend, so a dead backend means no catalog).
 *
 * The fallback is a full sentence; i18next's last-resort humanizer would render
 * a missing key as a bare word ("Network"), which is far worse copy. Asking
 * i18next for THIS fallback (translateWithFallback → `defaultValue`) is what
 * makes the sentence win — a `translated === key` comparison never fires under
 * the global `parseMissingKeyHandler`.
 */
const translateErrorsNamespaceKey = (
  t: TranslateWithDefaultValue,
  key: string,
  fallbackCopy: string,
  interpolationValues?: Readonly<Record<string, string>>,
): string => translateWithFallback(t, `errors:${key}`, fallbackCopy, interpolationValues)

/**
 * Single resolver for client-side synthetic error CODES, used by both the
 * ApiException branch and the duck-typed Error branch of toBackendMessage:
 *   - transport codes (NETWORK_ERROR, BAD_RESPONSE, ...) → per-key copy
 *   - `stripe.<code>` (client-side Stripe.js errors) → catalog entry, or the
 *     generic friendly message — never the raw technical code
 * Returns undefined when the code is not synthetic (caller falls through).
 */
const resolveSyntheticErrorMessage = (
  code: string,
  t: TranslateWithDefaultValue,
): string | undefined => {
  const syntheticKey = SYNTHETIC_CODE_TO_ERROR_I18N_KEY[code]
  if (syntheticKey) {
    return translateErrorsNamespaceKey(t, syntheticKey, SYNTHETIC_ERROR_FALLBACK_COPY[syntheticKey])
  }
  if (code.startsWith('stripe.')) {
    return translateErrorsNamespaceKey(t, code, SYNTHETIC_ERROR_FALLBACK_COPY.unexpected)
  }
  return undefined
}

/**
 * Canonical helper to surface backend error messages to the UI.
 *
 * Strategy (in order):
 *   1. Synthetic transport codes (NETWORK_ERROR, BAD_RESPONSE) → i18n key from `errors` namespace
 *   2. ApiException.details array (Zod field errors) → join messages with ' · '
 *   3. ApiException.message (backend-localized via Accept-Language) → return as-is
 *   4. Plain Error.message → return as-is
 *   5. String error → return as-is
 *   6. Fallback → `errors.unexpected` i18n key
 *
 * IMPORTANT: this helper deliberately does NOT accept a fallback string argument.
 * The backend is the single source of truth for error copy. If no message is
 * available, we use the single global i18n key `errors.unexpected` — never a
 * hardcoded English/Spanish literal. This prevents drift between frontend and
 * backend error text across features.
 *
 * The ONLY exception: when i18n itself cannot resolve a key (the catalog is
 * HTTP-loaded from the backend, so a dead backend means no catalog), the
 * hardcoded English `SYNTHETIC_ERROR_FALLBACK_COPY` is used — a raw i18n key
 * like `bad_response` must never reach the user.
 */

/**
 * The localized key for the first detail whose `code` names a rule we localize.
 * Returns undefined for every other payload, so the caller falls through.
 */
interface KnownRuleRejection {
  i18nKey: string
  /** The values the localized sentence interpolates; empty for most rules. */
  interpolationValues: Record<string, string>
}

const resolveKnownRuleRejection = (details: unknown): KnownRuleRejection | undefined => {
  if (!Array.isArray(details)) return undefined
  for (const detail of details as Array<{ code?: unknown; value?: unknown }>) {
    const code = detail?.code
    if (typeof code !== 'string') continue
    const i18nKey = BACKEND_RULE_CODE_TO_ERROR_I18N_KEY[code]
    if (i18nKey === undefined) continue

    // Only the entries this rule declares are read, so a payload cannot inject
    // arbitrary placeholders into the sentence.
    const declaredNames = BACKEND_RULE_CODE_TO_INTERPOLATION_KEYS[code] ?? []
    const interpolationValues: Record<string, string> = {}
    const value = detail.value
    if (typeof value === 'object' && value !== null) {
      for (const name of declaredNames) {
        const entry = (value as Record<string, unknown>)[name]
        if (typeof entry === 'string' && entry.length > 0) interpolationValues[name] = entry
      }
    }
    // A sentence whose placeholders cannot ALL be filled is worse than the
    // server's own prose: it would render «Las configuraciones «{{…}}» y …».
    // Fall through instead, and the caller keeps the backend message.
    if (Object.keys(interpolationValues).length < declaredNames.length) continue

    return { i18nKey, interpolationValues }
  }
  return undefined
}

/**
 * 404s: the KIND of the missing resource, as the backend declares it.
 *
 * Same mechanism as the rule codes above — the server puts a STABLE machine key
 * in the payload (`details.resourceKind`, e.g. `blockchain_template`) and the UI
 * maps it to localized copy. It exists because a 404 used to answer with the
 * sentence composed at the throw site, which was English AND interpolated the
 * internal id: "Blockchain template (0000…) not found" reached a Spanish admin
 * verbatim.
 *
 * Reads both `details` shapes the backend produces: the author-chosen OBJECT a
 * `createApiError(...)` carries, and the ErrorDetail ARRAY an ApplicationError
 * carries.
 */
const readResourceKindFromDetails = (details: unknown): string | undefined => {
  if (Array.isArray(details)) {
    for (const entry of details) {
      const kind = readResourceKindFromDetails(entry)
      if (kind !== undefined) return kind
    }
    return undefined
  }
  if (typeof details !== 'object' || details === null) return undefined

  const kind = (details as { resourceKind?: unknown }).resourceKind
  return typeof kind === 'string' && kind.length > 0 ? kind : undefined
}

/** Mirrors `resourceNotFound.generic` in the backend-served `errors` catalog. */
const GENERIC_RESOURCE_NOT_FOUND_COPY = 'The requested resource was not found.'

/**
 * El mensaje que el backend pone cuando NO tiene nada que decir por sí mismo:
 * `ValidationError.fromZodError` compone exactamente esta cadena y deja toda la
 * información en `details[]`. Es un CENTINELA, no copia para el usuario.
 *
 * ⚠️ No confundir con el compuesto de Fastify («Validation failed: exchange is
 * required»), que sí lleva información y por eso debe ganar.
 */
const GENERIC_VALIDATION_ENVELOPE_MESSAGE = 'Validation failed'

/**
 * ¿El mensaje de arriba aporta algo, o es el centinela vacío?
 *
 * ⚠️⚠️ De esto depende QUÉ frase lee el usuario. Antes los `details[]` ganaban
 * SIEMPRE, y eso invierte el resultado en los 218 rechazos donde el autor
 * escribió la frase a mano. Medido por la UI el 2026-08-14 al guardar un mapeo
 * multi-fichero:
 *
 *   message  →  «Configurations "Operaciones" and "Movimientos" share ≥80% of
 *                their expectedHeaders. Each must declare at least one
 *                discriminator so uploads can be routed unambiguously.»
 *   details  →  «overlapping headers require discriminators on both
 *                configurations»            ← esto es lo que se mostraba
 *
 * El detalle es jerga de esquema: no nombra las configuraciones, ni el umbral,
 * ni el remedio. Y el `message` es además el que el backend LOCALIZA por
 * Accept-Language; los `details` viajan en inglés crudo.
 */
const carriesAuthoredCopy = (message: string): boolean => {
  const limpio = message.trim()
  return limpio.length > 0 && limpio !== GENERIC_VALIDATION_ENVELOPE_MESSAGE
}

/**
 * ⚠️⚠️ EL SOBRE QUE ENUMERA CAMPOS NO ES COPIA REDACTADA.
 *
 * `validation-formatter.ts` compone dos formas muy distintas bajo el mismo
 * prefijo:
 *
 *   a) «Validation failed: exchange is required»
 *      → resumen HUMANO; los `details[]` traen jerga de esquema en inglés
 *        («must have required property 'exchange'»). Gana el sobre.
 *
 *   b) «Validation failed: incomingAmount: Maximum 8 decimal places allowed»
 *      → enumera `campo: mensaje` con el nombre INTERNO y el texto en INGLÉS
 *        CRUDO, mientras `details[]` trae la copia YA LOCALIZADA que la
 *        middleware tradujo («Máximo 8 decimales»). Ganan los detalles.
 *
 * Medido por la UI el 2026-08-16 escribiendo 12 decimales en una celda del
 * libro. El MISMO rechazo, por dos puertas:
 *
 *   modal    → «Errores de validación · Cantidad Entrante: Máximo 8 decimales»
 *   en línea → «Validation failed: incomingAmount: Maximum 8 decimal places
 *               allowed»          ← en inglés y enseñando el campo interno
 *
 * Las dos se distinguen por una marca objetiva: en (b) el resumen empieza por
 * el `field` de uno de los detalles seguido de dos puntos. No se mira el idioma
 * —eso sería adivinar—, sino la FORMA de la enumeración.
 *
 * ⚠️⚠️ LOS DOS PUNTOS NO SON LA ÚNICA FORMA. `describeIssue` compone la familia
 * de RANGO con un ESPACIO —`prefixFieldWithoutDuplication` devuelve
 * `` `${field} ${jerga del motor}` ``—, así que hasta el 2026-08-23 el rechazo
 * MEDIDO en la comparativa fiscal (#1896) se saltaba entero este guardián:
 *
 *     Validation failed: years.1 too big: expected number to be <=2026
 *
 * `years.1 too big: …` no empieza por `years.1:`, ganaba el sobre, y una
 * interfaz en español pintaba esa cadena de desarrollador tal cual.
 */
const enumerateFieldOfSomeDetail = (message: string, details: unknown): boolean => {
  if (!Array.isArray(details)) return false
  const resumen = message
    .trim()
    .slice(GENERIC_VALIDATION_ENVELOPE_MESSAGE.length + 1)
    .trim()
  return (details as Array<{ field?: unknown; code?: unknown }>).some((detail) => {
    if (typeof detail?.field !== 'string' || detail.field.length === 0) return false
    if (resumen.startsWith(`${detail.field}:`)) return true
    return summarizesRangeJargonOf(resumen, detail.field, detail.code)
  })
}

/**
 * Los códigos con los que `describeIssue` compone `` `${field} ${mensaje}` ``
 * en vez de la forma con dos puntos: `too_big`/`too_small` los emite Zod v4 y
 * `minLength`/`maxLength` los emite Ajv. En todos ellos el `${mensaje}` es la
 * cadena CRUDA del motor de validación, mientras el `details[]` gemelo trae la
 * copia que la middleware ya tradujo.
 */
const RANGE_VIOLATION_DETAIL_CODES = new Set(['too_big', 'too_small', 'minLength', 'maxLength'])

/**
 * ⚠️ La ÚNICA composición con espacio de `describeIssue` que NO es jerga.
 *
 * Un `z.string().min(1)` vacío también reporta `too_small`, pero el formateador
 * lo reescribe como el resumen HUMANO «exchange is required» (rama
 * `keyword === 'required' || isMinLengthOne`) y su detalle es el genérico
 * «Este campo es obligatorio», que PIERDE el nombre del campo. Ese es el caso
 * (a) de arriba: ahí el sobre debe seguir ganando.
 */
const REQUIRED_PREDICATE_AFTER_FIELD = 'is required'

/** ¿El resumen es «`${field} `» + la jerga de rango de ESTE detalle? */
const summarizesRangeJargonOf = (resumen: string, field: string, code: unknown): boolean => {
  if (typeof code !== 'string' || !RANGE_VIOLATION_DETAIL_CODES.has(code)) return false
  if (!resumen.startsWith(`${field} `)) return false
  return !resumen.startsWith(`${field} ${REQUIRED_PREDICATE_AFTER_FIELD}`)
}

/**
 * Localized copy for a 404, or undefined when this is not one (the caller then
 * falls through unchanged).
 *
 * A kind with no catalog entry resolves to the localized GENERIC sentence, so
 * adding a resource on the backend can never surface English or a raw id here.
 * A 404 that declares no kind is only rewritten when its message would leak an
 * internal identifier — otherwise the backend sentence is kept, since it often
 * carries detail the catalog cannot ("No active template for blockchain X").
 */
const resolveResourceNotFoundMessage = (
  error: ApiException,
  t: TranslateWithDefaultValue,
): string | undefined => {
  if (error.status !== 404) return undefined

  const genericMessage = translateErrorsNamespaceKey(
    t,
    'resourceNotFound.generic',
    GENERIC_RESOURCE_NOT_FOUND_COPY,
  )

  const resourceKind = readResourceKindFromDetails(error.details)
  if (resourceKind !== undefined) {
    return translateErrorsNamespaceKey(t, `resourceNotFound.${resourceKind}`, genericMessage)
  }
  if (INTERNAL_IDENTIFIER_PATTERN.test(error.message)) return genericMessage

  return undefined
}

/**
 * Marcadores que la copia de un CÓDIGO puede rellenar, y de dónde salen.
 *
 * POR QUÉ EXISTE: traducir por código SUSTITUYE la prosa del servidor por la
 * frase del catálogo. Para la mayoría de códigos eso es una mejora; para los
 * que NOMBRAN lo que falla es una pérdida, y si además la frase promete
 * nombrarlo, es una mentira. Medido por UI: el rechazo de transiciones decía
 * «El mensaje las nombra todas» sin nombrar ninguna, mientras el backend sí las
 * traía en su mensaje inglés.
 *
 * Se declaran AQUÍ, por código, y se leen sólo de `details`: un payload no
 * puede inyectar marcadores arbitrarios en la frase. Es el mismo contrato que
 * `BACKEND_RULE_CODE_TO_INTERPOLATION_KEYS`, incluida su regla de oro — si no
 * se pueden rellenar TODOS, no se pinta la frase.
 */
const BACKEND_CODE_TO_INTERPOLATION_KEYS: Record<string, readonly string[]> = {
  SWEEP_TRANSITION_TARGET_NOT_FOUND: ['broken', 'phases'],
  SWEEP_PHASE_NOT_FOUND: ['phase', 'phases'],
}

/**
 * Valores para los marcadores de un código: `{}` si no declara ninguno (el
 * comportamiento de siempre), o `undefined` si declara alguno que `details` no
 * trae — y entonces el llamante debe quedarse con la prosa del servidor, porque
 * una frase con «{{broken}}» sin sustituir es peor que el inglés completo.
 */
const resolveCodeInterpolationValues = (
  code: string,
  details: unknown,
): Readonly<Record<string, string>> | undefined => {
  const declaredNames = BACKEND_CODE_TO_INTERPOLATION_KEYS[code]
  if (declaredNames === undefined) return {}
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return undefined

  const values: Record<string, string> = {}
  for (const name of declaredNames) {
    const entry = (details as Record<string, unknown>)[name]
    if (typeof entry === 'string' && entry.length > 0) values[name] = entry
  }
  return Object.keys(values).length === declaredNames.length ? values : undefined
}

/**
 * Localized copy for an error whose CODE this app localizes, or `undefined` for
 * every other error.
 *
 * Same registries `toBackendMessage` uses — synthetic transport codes, the
 * per-rule `details[].code` map, and the top-level backend-code map — exposed
 * separately for callers that own a localized GENERIC fallback and must not
 * fall through to `toBackendMessage`'s raw `error.message` (which is hardcoded
 * English on exactly the codes worth naming, and frequently carries an internal
 * id). Those callers ask "do you have specific copy for this?" and keep their
 * own sentence when the answer is no.
 *
 * Adding a code is opt-in: an unregistered code returns `undefined`, so no
 * existing message can change.
 */
export const resolveLocalizedBackendCodeMessage = (
  error: unknown,
  t: TranslateWithDefaultValue,
): string | undefined => {
  if (!(error instanceof ApiException) || typeof error.code !== 'string') return undefined

  const syntheticMessage = resolveSyntheticErrorMessage(error.code, t)
  if (syntheticMessage !== undefined) return syntheticMessage

  // Same resolution `toBackendMessage` performs, interpolation included, so the
  // two entry points can never render one rejection two ways.
  const rule = resolveKnownRuleRejection(error.details)
  if (rule !== undefined) {
    return translateErrorsNamespaceKey(
      t,
      rule.i18nKey,
      error.message.length > 0 ? error.message : SYNTHETIC_ERROR_FALLBACK_COPY.unexpected,
      rule.interpolationValues,
    )
  }

  const backendKey = BACKEND_CODE_TO_ERROR_I18N_KEY[error.code]
  if (backendKey === undefined) return undefined

  // Un código que DECLARA marcadores y no los puede rellenar se comporta como
  // un código sin copia: este resolutor promete «o copia específica, o nada»,
  // y sus llamantes tienen su propia frase genérica.
  const interpolationValues = resolveCodeInterpolationValues(error.code, error.details)
  if (interpolationValues === undefined) return undefined

  // The fallback is the backend's own message with any internal id stripped —
  // identical treatment to toBackendMessage, so the two can never disagree.
  const safeMessage =
    error.message.length > 0 ? toCustomerSafeFallbackMessage(error.message) : undefined
  return translateErrorsNamespaceKey(
    t,
    backendKey,
    safeMessage ?? SYNTHETIC_ERROR_FALLBACK_COPY.unexpected,
    interpolationValues,
  )
}

export const toBackendMessage = (error: unknown, t: TranslateWithDefaultValue): string => {
  if (error instanceof ApiException) {
    // Synthetic transport errors that the UI layer localizes (backend cannot).
    // The resolver uses the `errors:` namespace-prefixed form so resolution
    // works regardless of which namespace the caller bound with
    // `useTranslation(...)`.
    if (typeof error.code === 'string') {
      const syntheticMessage = resolveSyntheticErrorMessage(error.code, t)
      if (syntheticMessage !== undefined) return syntheticMessage

      // Known backend business-error codes whose inline message is hardcoded
      // English → localize via the `errors` catalog, falling back to the
      // backend message (never a raw i18n key) when the catalog is unavailable.
      // A SPECIFIC rule beats the generic business-rule copy. Only rules listed
      // in the catalog above take this path; every other code behaves as before.
      const rule = resolveKnownRuleRejection(error.details)
      if (rule !== undefined) {
        // The backend sentence is the fallback: it is English, but it names the
        // same things the localized copy does, so a catalog that never arrived
        // still leaves the admin with an actionable message.
        return translateErrorsNamespaceKey(
          t,
          rule.i18nKey,
          error.message.length > 0 ? error.message : SYNTHETIC_ERROR_FALLBACK_COPY.unexpected,
          rule.interpolationValues,
        )
      }

      const backendKey = BACKEND_CODE_TO_ERROR_I18N_KEY[error.code]
      if (backendKey !== undefined) {
        // The fallback is the backend's own message; strip any internal ids it
        // interpolated so a missing catalog can never expose one to the user.
        const safeMessage =
          error.message.length > 0 ? toCustomerSafeFallbackMessage(error.message) : undefined
        // Aquí SÍ hay a dónde caer: la prosa del servidor, que es inglesa pero
        // nombra lo que falla. Entre un español con agujeros y un inglés
        // completo, gana el que le dice al operador qué corregir.
        const interpolationValues = resolveCodeInterpolationValues(error.code, error.details)
        if (interpolationValues === undefined) {
          return safeMessage ?? SYNTHETIC_ERROR_FALLBACK_COPY.unexpected
        }
        return translateErrorsNamespaceKey(
          t,
          backendKey,
          safeMessage ?? SYNTHETIC_ERROR_FALLBACK_COPY.unexpected,
          interpolationValues,
        )
      }
    }

    // Resource-kind copy for 404s. Sits AFTER the per-code lookups above so a
    // code with its own localized sentence (e.g. matching.transaction_not_found)
    // keeps it, and BEFORE the raw-message fallbacks below.
    const resourceNotFoundMessage = resolveResourceNotFoundMessage(error, t)
    if (resourceNotFoundMessage !== undefined) return resourceNotFoundMessage

    // La frase REDACTADA gana al detalle de esquema. Sólo cuando el mensaje es
    // el centinela vacío («Validation failed») los `details[]` son la única
    // información que hay, y entonces sí mandan ellos.
    if (
      carriesAuthoredCopy(error.message) &&
      !enumerateFieldOfSomeDetail(error.message, error.details)
    ) {
      return error.message
    }

    if (Array.isArray(error.details) && error.details.length > 0) {
      const messages = (error.details as Array<{ message?: string }>)
        .map((d) => d?.message)
        .filter((m): m is string => typeof m === 'string' && m.length > 0)
      if (messages.length > 0) return messages.join(' · ')
    }
    if (error.message) return error.message
  }
  // Duck-typed synthetic-code check for non-ApiException Error subclasses
  // (e.g. ApiClientError from the support module). Importing those classes
  // here would create shared -> feature coupling, so we match by shape.
  if (error instanceof Error && typeof (error as { code?: unknown }).code === 'string') {
    const code = (error as { code?: string }).code
    if (typeof code === 'string') {
      const syntheticMessage = resolveSyntheticErrorMessage(code, t)
      if (syntheticMessage !== undefined) return syntheticMessage
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'string' && error.length > 0) {
    return error
  }
  return translateErrorsNamespaceKey(t, 'unexpected', SYNTHETIC_ERROR_FALLBACK_COPY.unexpected)
}

/**
 * Non-i18n variant of toBackendMessage for use in stores/services that cannot
 * call React hooks. Loses localization of synthetic transport codes but still
 * surfaces backend-localized `ApiException.message` and flattens `details`.
 */
export const toBackendMessageNoI18n = (error: unknown): string => {
  if (error instanceof ApiException) {
    // Misma prioridad que `toBackendMessage`: si divergieran, el mismo rechazo
    // se leería distinto según qué pantalla lo pinte.
    if (
      carriesAuthoredCopy(error.message) &&
      !enumerateFieldOfSomeDetail(error.message, error.details)
    ) {
      return error.message
    }

    if (Array.isArray(error.details) && error.details.length > 0) {
      const messages = (error.details as Array<{ message?: string }>)
        .map((d) => d?.message)
        .filter((m): m is string => typeof m === 'string' && m.length > 0)
      if (messages.length > 0) return messages.join(' · ')
    }
    if (error.message) return error.message
  }
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error.length > 0) return error
  return ''
}

export const handleApiError = (error: unknown): ApiError => {
  if (error !== null && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>
    if (
      errorObj['response'] !== undefined &&
      typeof errorObj['response'] === 'object' &&
      errorObj['response'] !== null
    ) {
      const response = errorObj['response'] as Record<string, unknown>
      const data = response['data'] as Record<string, unknown> | undefined
      // Server responded with error status. Preserve empty message when the
      // backend didn't supply one so toBackendMessage can translate via code.
      return createApiError(
        typeof response['status'] === 'number' ? response['status'] : 500,
        typeof data?.['message'] === 'string' ? data['message'] : '',
        typeof data?.['code'] === 'string' ? data['code'] : undefined,
        data,
      )
    } else if (errorObj['request'] !== undefined) {
      // Transport-level network failure; empty message + code drives i18n resolution
      return createApiError(0, '', 'NETWORK_ERROR')
    } else {
      const message = typeof errorObj['message'] === 'string' ? errorObj['message'] : ''
      return createApiError(500, message, 'UNKNOWN_ERROR')
    }
  }
  return createApiError(500, '', 'UNKNOWN_ERROR')
}
